import { useState } from "react"
import { ZonePolicyDueDateType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import * as z from "zod"

import { addZonePolicyDue, getZoneDropdown } from "@/lib/api/billing"
import { useToast } from "@/hooks/use-toast"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Header } from "@/components/header"

import { ComboboxSearch } from "../combobox-search"
import { Icons } from "../icons"
import { Button } from "../ui/button"
import { Card } from "../ui/card"
import { DialogClose } from "../ui/dialog"
import { Input } from "../ui/input"

const formSchema = z.object({
  zone_id: z.coerce.number().min(1, "Zone is required"),
  before_due_reminder: z.coerce
    .number()
    .min(1, "Before Due Reminder is required"),
  overdue_reminder: z.coerce.number().min(1, "Over Due Reminder is required"),
  disconnection_reminder: z.coerce
    .number()
    .min(1, "Disconnection Reminder is required"),
})

export default function ZonesPolicyForm({
  setShowModal,
}: {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [zoneId, setZoneId] = useState<number | undefined>()
  const results = useQueries({
    queries: [
      {
        queryKey: ["zones"],
        queryFn: getZoneDropdown,
      },
    ],
  })

  const [zones] = results.map((result) => result.data || [])

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      zone_id: 0,
      before_due_reminder: 0,
      overdue_reminder: 0,
      disconnection_reminder: 0,
    },
  })

  const mutation = useMutation({
    mutationFn: async (data: ZonePolicyDueDateType) => {
      await addZonePolicyDue(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["policyDetails"] })
      toast({
        description: "New zone policy due added successfully.",
      })
      setShowModal(false)
    },
    onError: async (error: any) => {
      const response = error.response
      let errorMessage = "Please try again."

      if (response && response.status === 400) {
        const errorData = await response.json()
        errorMessage = errorData.message || JSON.stringify(errorData)
      }
      toast({
        title: "Something went wrong!",
        description: errorMessage,
        variant: "destructive",
      })
    },
  })

  function handleSubmit(values: z.infer<typeof formSchema>) {
    mutation.mutate(values as Omit<ZonePolicyDueDateType, "id">)
  }

  return (
    <div className="px-2">
      <Header
        subroutes={[
          {
            route: "/zone-settings/zone-policy-due-date",
            label: "Policy Due Date",
          },
        ]}
      >
        Add Policy Due Date
      </Header>
      <DropdownMenuSeparator className="my-4" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="grid grid-cols-1 gap-4"
        >
          <Card className="my-4 gap-4 p-6">
            <FormField
              control={form.control}
              name="zone_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Zone Id</FormLabel>
                  <FormMessage />
                  <FormControl>
                    <ComboboxSearch
                      field={field}
                      options={zones}
                      placeholder="Select zone"
                      onSelectId={(id?: string | number) =>
                        setZoneId(id as number)
                      }
                      idAsValue
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="before_due_reminder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Before Due Reminder</FormLabel>
                  <FormMessage />
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="overdue_reminder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Over Due Reminder</FormLabel>
                  <FormMessage />
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="disconnection_reminder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Disconnection Reminder</FormLabel>
                  <FormMessage />
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          </Card>
          <div className="flex flex-row justify-end gap-2 md:mr-4">
            <DialogClose>
              <Button variant="outline" type="button" className="w-28">
                Cancel
              </Button>
            </DialogClose>
            <Button
              disabled={mutation.isPending}
              type="submit"
              className="w-28"
            >
              {mutation.isPending && (
                <Icons.loader className="mr-2 animate-spin" />
              )}
              Add
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
